import { useState, useEffect } from 'react';

interface UseGoogleMapsOptions {
  apiKey: string;
  libraries?: string[];
}

interface UseGoogleMapsReturn {
  isLoaded: boolean;
  error: string | null;
}

let isScriptLoaded = false;
let isScriptLoading = false;
let loadPromise: Promise<void> | null = null;

export const useGoogleMaps = ({ apiKey, libraries = ['marker'] }: UseGoogleMapsOptions): UseGoogleMapsReturn => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Si l'API est déjà chargée, retourner immédiatement
    if (window.google?.maps) {
      setIsLoaded(true);
      return;
    }

    // Si le script est en cours de chargement, attendre qu'il soit terminé
    if (isScriptLoading && loadPromise) {
      loadPromise
        .then(() => setIsLoaded(true))
        .catch(err => setError(err.message));
      return;
    }

    // Si le script n'est pas encore chargé, le charger
    if (!isScriptLoaded && !isScriptLoading) {
      isScriptLoading = true;

      loadPromise = new Promise((resolve, reject) => {
        try {
          // Définir la fonction de callback globale
          const callbackName = `googleMapsCallback_${Date.now()}`;
          (window as any)[callbackName] = async () => {
            try {
              // Charger explicitement les bibliothèques nécessaires
              await Promise.all([
                google.maps.importLibrary("marker"),
                google.maps.importLibrary("core"),
                google.maps.importLibrary("places")
              ]);
              
              isScriptLoaded = true;
              isScriptLoading = false;
              setIsLoaded(true);
              delete (window as any)[callbackName];
              resolve();
            } catch (err) {
              reject(err);
            }
          };

          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=beta&callback=${callbackName}&libraries=${libraries.join(',')}&loading=async`;
          script.async = true;
          script.defer = true;

          script.addEventListener('error', () => {
            delete (window as any)[callbackName];
            const error = new Error('Failed to load Google Maps');
            setError(error.message);
            reject(error);
          });

          // Insérer le script au début du head pour le charger en priorité
          const firstScript = document.getElementsByTagName('script')[0];
          firstScript.parentNode?.insertBefore(script, firstScript);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          reject(err);
        }
      });

      loadPromise.catch(() => {
        isScriptLoading = false;
        isScriptLoaded = false;
      });
    }
  }, [apiKey, libraries.join(',')]);

  return { isLoaded, error };
};